import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartLegend,
  ChartArea
} from '@progress/kendo-react-charts';
import 'hammerjs';
import '@progress/kendo-theme-material/dist/all.css';
import {
  CurrencyNumberFormatString,
  PercentageNumberFormatString
} from 'src/utils/numbers';
import { APARTMENT_OBJECT_TYPE, COLORS } from 'src/constants';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  label: {
    marginLeft: theme.spacing(1)
  }
}));

const SummaryChart = React.memo(function SummaryChart({ consumption, className, ...rest }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!consumption) {
    return null;
  }

  const labelContent = props => {
    let formatedNumber = CurrencyNumberFormatString(props.dataItem.value);
    let percentageNumber = PercentageNumberFormatString(
      props.dataItem.percentage
    );
    return `${props.dataItem.category} ${formatedNumber} (${percentageNumber})`;
  };

  const { periodSummary: summary } = consumption;
  const prices = {
    coldWater: summary.coldwater.priceSum,
    hotWater: summary.hotwater.priceSum,
    electricity: summary.electricity.priceSum,
    heatEnergy: summary.heatenergy.priceSum,
    evCharging: summary.evCharging.priceSum,
    kasEnergy: summary.kasEnergy.priceSum
  };

  const totalsum =
    Object.keys(prices).reduce((acc, curr) => (acc += Math.abs(prices[curr])), 0) || 1;

  const summarydata = [
    {
      category: 'Kallvatten',
      value: prices.coldWater,
      percentage: Math.abs(prices.coldWater) / totalsum,
      color: COLORS.COLDWATER,
      visible: consumption.coldWaterSensorsExists
    },
    {
      category: 'Varmvatten',
      value: prices.hotWater,
      percentage: Math.abs(prices.hotWater) / totalsum,
      color: COLORS.HOTWATER,
      visible: consumption.hotWaterSensorsExists
    },
    {
      category: 'El',
      value: prices.electricity,
      percentage: Math.abs(prices.electricity) / totalsum,
      color: COLORS.ELECTRICITY,
      visible: consumption.electicitySensorsExists
    },
    {
      category: 'Värmeenergi',
      value: prices.heatEnergy,
      percentage: Math.abs(prices.heatEnergy) / totalsum,
      color: COLORS.HEATENERGY,
      visible: consumption.heatEnergySensorsExists
    },
    {
      category: 'Elbilsladdning',
      value: prices.evCharging,
      percentage: Math.abs(prices.evCharging) / totalsum,
      color: COLORS.EVCHARGING,
      visible: consumption.evChargingSensorsExists
    },
    {
      category: 'Komfortavräkning',
      value: prices.kasEnergy,
      percentage: Math.abs(prices.kasEnergy) / totalsum,
      color: COLORS.KAS,
      visible: consumption.measurementObjectTypes?.includes(
        APARTMENT_OBJECT_TYPE.KAS
      )
    }
  ];

  const allValuesEmpty = summarydata.every(
    item => item.value === null || item.value === 0
  );

  if (allValuesEmpty) {
    // Visa tom paj direkt om ingen consumption finns
    return (
      <Card className={clsx(classes.root, className)} {...rest}>
        <Box flexGrow={1}>
          <Typography
            component="h3"
            gutterBottom
            variant="overline"
            color="textSecondary"
          >
            Fördelning av kostnader
          </Typography>
          <Chart>
            <ChartArea background={theme.palette.background.paper} />
            <ChartSeries>
              <ChartSeriesItem
                data={[{ category: 'Ingen data', value: 1, color: '#ccc' }]}
                field="value"
                type="pie"
                labels={{ visible: false }}
              />
            </ChartSeries>
          </Chart>
        </Box>
      </Card>
    );
  }

  const formatLabel = (props) => {
    const val = props.dataItem.value;
    const absVal = Math.abs(val);
    const statusText = val === 0 ? "" : val < 0 ? 'Att betala' : 'Att få tillbaks';
    const formattedValue = CurrencyNumberFormatString(absVal);
    // Visar kategori + belopp + status (enkel och tydlig)
    return `${props.dataItem.category}: ${formattedValue}\n${statusText}`;
  };

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <Box flexGrow={1}>
        <Typography
          component="h3"
          gutterBottom
          variant="overline"
          color="textSecondary"
        >
          Fördelning av kostnader
        </Typography>
        <Chart>
          <ChartArea background={theme.palette.background.paper} />
          <ChartLegend visible={false} />
          <ChartSeries>
            <ChartSeriesItem
              data={summarydata}
              field="value"
              type="pie"
              labels={{ visible: !isMobile, content: formatLabel, background: 'transparent', color: theme.palette.text.primary }}
            ></ChartSeriesItem>
          </ChartSeries>
        </Chart>
        {isMobile && !allValuesEmpty && (
          <Box mt={2} display="flex" flexDirection="column" gap={1}>
            {summarydata
              .filter(item => item.visible) // bara de som ska visas
              .map(item => (
                <Box key={item.category} display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      width={12}
                      height={12}
                      bgcolor={item.color}
                      borderRadius="2px"
                    />
                    <Typography variant="body2">{item.category}</Typography>
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    {CurrencyNumberFormatString(Math.abs(item.value))}{" "}
                    {item.value < 0 ? "Att betala" : item.value > 0 ? "Att få tillbaks" : ""}
                  </Typography>
                </Box>
              ))}
          </Box>
        )}
      </Box>
    </Card>
  );
});

SummaryChart.propTypes = {
  className: PropTypes.string
};

export default SummaryChart;
